import { z } from 'zod';

import type { CreateUserInputFE, UpdateUserInputFE, UserActionResultFE, UserFE, UserStatusFE } from '../systemAdmin';
import type { MsSaveUserTO, MsUserTeamTO, MsUserTO, MsUserUpdateTO } from './msWireSpec';
import {
    emptyObjectToArray,
    fromPlainEnum,
    makeSanitizeWire,
    safeOptional,
    safeRedactedOptional,
    toNamedRefs,
} from './restMapping';
import { rolesFromRestV1 } from './roleFromRestV1';

/**
 * REST <-> domain mapping for users, served by the management service's `UsersApi`:
 * `GET /api/v1/users` -> {@link usersFromRestV1}, `GET|POST|PUT /api/v1/users[/{id}]` ->
 * {@link userFromRestV1}. Every personal field degrades through `safeRedactedOptional`.
 */

const label = (field: string) => `userFromRestV1.${field}`;

/** `createdAt` / `updatedAt` — camelCase, which is why the shared `sanitizeWire` is not used. */
const USER_DATE_KEYS = new Set(['createdAt', 'updatedAt']);

const sanitizeUserWire = makeSanitizeWire(USER_DATE_KEYS);

const KNOWN_USER_STATUSES: readonly UserStatusFE[] = ['INVITED', 'ACTIVE', 'INACTIVE'];

const WireUserTeamObjectSchema = z.object({
    id: safeOptional(z.string(), label('teams[].id')),
    name: safeOptional(z.string(), label('teams[].name')),
});

/**
 * `firstName`, `lastName`, `phone`, `email`, `city` and `country` are personal data, so they use
 * `safeRedactedOptional`: the degradation line names the field and nothing else. `roles` delegates
 * to `rolesFromRestV1`, which carries no personal data, so it is parsed loosely here.
 */
const WireUserObjectSchema = z.object({
    id: z.string().min(1),
    firstName: safeRedactedOptional(z.string(), label('firstName')),
    lastName: safeRedactedOptional(z.string(), label('lastName')),
    phone: safeRedactedOptional(z.string(), label('phone')),
    email: safeRedactedOptional(z.string(), label('email')),
    city: safeRedactedOptional(z.string(), label('city')),
    country: safeRedactedOptional(z.string(), label('country')),
    createdAt: safeOptional(z.string(), label('createdAt')),
    updatedAt: safeOptional(z.string(), label('updatedAt')),
    status: safeOptional(z.string(), label('status')),
    teams: safeOptional(z.preprocess(emptyObjectToArray, z.array(WireUserTeamObjectSchema)), label('teams')),
    roles: safeOptional(z.preprocess(emptyObjectToArray, z.array(z.unknown())), label('roles')),
});

/** Wire schema for a `UserTO`, exported for `mocks/tests/restContract.test.ts`. */
export const WireUserSchema = z.preprocess(sanitizeUserWire, WireUserObjectSchema);

type WireUserObject = z.infer<typeof WireUserObjectSchema>;
type WireUserTeamObject = z.infer<typeof WireUserTeamObjectSchema>;

type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

export type UserWireKeysExistOnSpec = {
    user: KeysExistOn<WireUserObject, MsUserTO>;
    userTeam: KeysExistOn<WireUserTeamObject, MsUserTeamTO>;
    userCreate: KeysExistOn<MsSaveUserTO, CreateUserInputFE>;
    userUpdate: KeysExistOn<MsUserUpdateTO, UpdateUserInputFE>;
};

export const userFromRestV1 = (raw: unknown): UserFE => {
    const wire = WireUserSchema.parse(raw);
    return {
        id: wire.id,
        firstName: wire.firstName ?? '',
        lastName: wire.lastName ?? '',
        email: wire.email ?? '',
        phone: wire.phone ?? '',
        city: wire.city ?? '',
        country: wire.country ?? '',
        status: fromPlainEnum(wire.status, KNOWN_USER_STATUSES),
        createdAt: wire.createdAt ?? '',
        updatedAt: wire.updatedAt ?? '',
        teams: toNamedRefs(wire.teams, label('teams[].id')),
        roles: rolesFromRestV1(wire.roles ?? []),
    };
};

/**
 * A bad entry is dropped with the label alone — never the entry and never the Zod issue, both of
 * which would carry the person's name, phone number and email address.
 */
export const usersFromRestV1 = (raw: unknown): UserFE[] => {
    if (!Array.isArray(raw)) {
        console.error(`Zod catch triggered (${label('list')}) — received ${typeof raw}`);
        return [];
    }
    return raw.reduce<UserFE[]>((acc, entry) => {
        try {
            acc.push(userFromRestV1(entry));
        } catch {
            console.error(`Zod catch triggered (${label('list[]')}) — entry redacted`);
        }
        return acc;
    }, []);
};

export const toUserCreateWire = (input: CreateUserInputFE): MsSaveUserTO => ({
    firstName: input.firstName,
    lastName: input.lastName,
    phone: input.phone,
    email: input.email,
    city: input.city,
    country: input.country,
    teams: input.teams,
    roles: input.roles,
});

/** `id` travels in the path, and `email` is not part of `UserUpdateTO`. */
export const toUserUpdateWire = (input: UpdateUserInputFE): MsUserUpdateTO => ({
    firstName: input.firstName,
    lastName: input.lastName,
    phone: input.phone,
    country: input.country,
    city: input.city,
    roles: input.roles,
    teams: input.teams,
});

/** Delete and reset-password declare no response body, so the status code is the whole result. */
export const userActionResultFromRestV1 = (status: number): UserActionResultFE => ({ statusCode: status });
